import { useCallback } from 'react';
import {
  isServiceCategoryElement,
  isProductCategoryElement,
  isSystemCategory,
} from '@shared/helpers/element-category.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { hasChildren } from '@shared/helpers/recursive-element.helpers';
import { IElement, OneOfChildElements } from '@shared/models/project.model';
import { useSelectedElement, useSelectedVersion } from '@client/store/ui';
import { useIsConfirmed } from './use-confirm.hook';
import { useRecipeContextElement } from '@client/hooks/use-recipe-context-element.hooks';
import {
  useRecipe,
  useRecipeIsModified,
} from '@features/recipes/use-recipes.hooks';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';

export const useNewContentConfirmation = () => {
  const confirm = useIsConfirmed();
  const selectedElement = useSelectedElement();
  const recipeElement = useRecipeContextElement(selectedElement);
  const recipe = useRecipe(recipeElement ?? selectedElement);
  const recipeIsModified = useRecipeIsModified(
    recipeElement ?? selectedElement,
  );

  return useCallback(
    /**
     * Confirms with the user before potentially discarding content or changes
     * @param element - The element that is being modified
     * @param categoryId - New category id that is about to be applied to the element
     * @returns Promise<boolean> - true if the user confirms or no confirmation is needed, false if cancelled
     */
    async (element: IElement, categoryId?: ElementCategoryID) => {
      const hasGeneratedContent =
        isServiceCategoryElement(element) || isProductCategoryElement(element);

      const hasUnsavedContent =
        hasChildren(element) && !element.recipe_id && !hasGeneratedContent;

      // If the category is about to change to a system category, the element will keep its content and no confirm is needed
      if (!isSystemCategory(categoryId) && hasUnsavedContent) {
        return confirm({
          title: 'Discard current content?',
          description: `The current content on element "${getElementName(element)}" will be lost. This action cannot be undone.`,
          cancellationText: 'Cancel',
          confirmationText: 'Discard content',
        });
      }
      if (
        recipe &&
        recipeIsModified.status === 'ready' &&
        recipeIsModified.isModified
      ) {
        return confirm({
          title: 'Discard changes?',
          description: `Unsaved changes on recipe "${recipe.name}" will be lost. This action cannot be undone.`,
          cancellationText: 'Cancel',
          confirmationText: 'Discard changes',
        });
      }
      return true;
    },
    [confirm, recipe, recipeIsModified],
  );
};

export const useBuildingVersionChildElements = (): OneOfChildElements[] =>
  useSelectedVersion()?.elements ?? getEmptyFrozenArray();
